import * as angular from "angular";
import "angular-ui-bootstrap";
import {
	AlternativeState,
	cloneValue,
	dataAtPath,
	FormRuntime,
	FormUiSchema,
	JsonSchemaDefinition
} from "../runtime";
import { TimeZoneContext } from "../TimeZoneContext";
import type { ControlState } from "./control-state";
import alternativeConfirmation from "./alternative-confirmation";
import template from "./alternative-control.html";

interface AlternativeChoice {
	value: number;
	label: string;
}

interface AlternativeBranch {
	index: number;
	schema: JsonSchemaDefinition;
	ui: FormUiSchema;
}

interface ConfirmationInstance {
	result: PromiseLike<unknown>;
	dismiss(reason: string): void;
}

interface ConfirmationService {
	open(options: {
		component: string;
		size: string;
		backdrop: string;
		resolve: {
			label(): string;
		};
	}): ConfirmationInstance;
}

interface PendingChange {
	runtime: FormRuntime;
	schema: JsonSchemaDefinition;
	path: string;
	value: unknown;
	index: number;
	modal: ConfirmationInstance;
}

export interface AlternativeControlController {
	control: ControlState;
}

// AngularJS owns the merged binding properties.
// eslint-disable-next-line no-redeclare
export class AlternativeControlController implements angular.IController {
	public static $inject = ["$scope", "$uibModal"];

	public alternatives?: AlternativeState;

	public choices: AlternativeChoice[] = [];

	public branches: AlternativeBranch[] = [];

	public selected?: number;

	private pending?: PendingChange;

	private destroyed = false;

	public constructor(
		private readonly $scope: angular.IScope,
		private readonly $uibModal: ConfirmationService
	) {}

	public $onChanges(): void {
		if(this.pending && !this.isCurrent(this.pending)) {
			this.cancelPending();
		}

		this.refresh();
	}

	public $onDestroy(): void {
		this.destroyed = true;
		this.cancelPending();
	}

	public change(): void {
		const index = this.selected;
		this.selected = this.alternatives?.index;

		if(index === undefined || !this.alternatives?.options[index] || index === this.alternatives.index || !this.control.canEdit()) {
			return;
		}

		this.cancelPending();
		const runtime = this.control.session.runtime;
		const change = runtime.getAlternativeChange(this.control.schema, this.control.path, index);

		if(!change.confirm) {
			this.select(index);
			return;
		}

		const modal = this.$uibModal.open({
			component: "jsonSchemaAlternativeConfirmation",
			size: "sm",
			backdrop: "static",
			resolve: {
				label: () => this.alternatives?.options[index]?.label ?? "the selected option"
			}
		});
		const pending: PendingChange = {
			runtime,
			schema: this.control.schema,
			path: this.control.path,
			value: cloneValue(this.control.value),
			index,
			modal
		};
		this.pending = pending;

		modal.result.then(() => {
			this.$scope.$evalAsync(() => {
				if(this.pending === pending && this.isCurrent(pending)) {
					this.pending = undefined;
					this.select(pending.index);
				}
			});
		}, () => {
			if(this.pending === pending) {
				this.pending = undefined;
			}
		});
	}

	private select(index: number): void {
		if(!this.control.canEdit()) {
			return;
		}

		const runtime = this.control.session.runtime;
		const change = runtime.getAlternativeChange(this.control.schema, this.control.path, index);
		runtime.selectAlternative(this.control.schema, this.control.path, index);

		if(change.replace) {
			this.control.session.timeZones.replaceSubtree(this.control.path, new TimeZoneContext());
		}

		this.control.change(change.value);
		this.refresh();
	}

	private refresh(): void {
		this.alternatives = this.control.session.runtime.getAlternatives(this.control.schema, this.control.path);
		this.selected = this.alternatives?.index;
		this.choices = this.alternatives?.options.map((option, value) => ({
			value,
			label: option.label
		})) ?? [];
		const option = this.alternatives?.options[this.selected ?? 0];

		this.branches = option ? [{
			index: this.selected ?? 0,
			schema: option.schema,
			ui: {
				type: "Control",
				scope: "#",
				label: this.alternatives?.kind === "type" ? this.control.title || option.label : option.label,
				options: this.control.ui.options
			}
		}] : [];
	}

	private isCurrent(pending: PendingChange): boolean {
		return !this.destroyed
			&& pending.runtime === this.control.session.runtime
			&& pending.schema === this.control.schema
			&& pending.path === this.control.path
			&& angular.equals(pending.value, dataAtPath(pending.runtime.data, pending.path))
			&& this.control.canEdit();
	}

	private cancelPending(): void {
		const pending = this.pending;
		this.pending = undefined;
		pending?.modal.dismiss("form-context-changed");
	}
}

export default angular.module("vesl.extensions.json-schema.alternative-control", ["ui.bootstrap", alternativeConfirmation])
	.component("jsonSchemaAlternativeControl", {
		template,
		controller: AlternativeControlController,
		bindings: { control: "<" }
	})
	.name;
